import React from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { getCanonicalUrl } from "../utils/seoConfig";

const llmInfoJsonLd = (pageUrl) =>
  JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "Predsense - machine-readable summary for AI systems",
        description:
          "Canonical facts about Predsense (PredSense GmbH): products, contact, and key URLs for retrieval and citation.",
        inLanguage: ["en", "de"],
        publisher: { "@id": "https://www.predsense.ai/#organization" },
        about: { "@id": "https://www.predsense.ai/#organization" },
      },
      {
        "@type": "Dataset",
        "@id": `${pageUrl}#dataset`,
        name: "Predsense entity and product facts",
        description:
          "Structured factual statements about Predsense intended for language models and answer engines.",
        url: pageUrl,
        creator: { "@id": "https://www.predsense.ai/#organization" },
      },
    ],
  });

const LlmInfo = () => {
  const { t, i18n } = useTranslation();
  const pageUrl = getCanonicalUrl("/llm-info");
  const orgUrl = "https://www.predsense.ai";

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t("LlmInfoPageTitle")}</title>
        <meta name="description" content={t("LlmInfoMetaDescription")} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={t("LlmInfoPageTitle")} />
        <meta
          property="og:description"
          content={t("LlmInfoMetaDescription")}
        />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={t("LlmInfoPageTitle")} />
        <meta
          name="twitter:description"
          content={t("LlmInfoMetaDescription")}
        />
        <script type="application/ld+json">{llmInfoJsonLd(pageUrl)}</script>
      </Helmet>
      <div className="legal-section llm-info-page">
        <h1>{t("LlmInfoH1")}</h1>
        <div>
          <p className="llm-info-lead">{t("LlmInfoLead")}</p>

        <section aria-labelledby="llm-answer-heading">
          <h2 id="llm-answer-heading">{t("LlmInfoSectionAnswerTitle")}</h2>
          <p>{t("LlmInfoSectionAnswerP1")}</p>
          <p>{t("LlmInfoSectionAnswerP2")}</p>
        </section>

        <section aria-labelledby="llm-entity-heading">
          <h2 id="llm-entity-heading">{t("LlmInfoSectionEntityTitle")}</h2>
          <ul className="llm-info-facts">
            <li>
              <strong>{t("LlmInfoFactLegalName")}</strong>{" "}
              {t("LlmInfoFactLegalNameValue")}
            </li>
            <li>
              <strong>{t("LlmInfoFactBrand")}</strong>{" "}
              {t("LlmInfoFactBrandValue")}
            </li>
            <li>
              <strong>{t("LlmInfoFactAddress")}</strong>{" "}
              {t("LlmInfoFactAddressValue")}
            </li>
            <li>
              <strong>{t("LlmInfoFactCountry")}</strong>{" "}
              {t("LlmInfoFactCountryValue")}
            </li>
            <li>
              <strong>{t("LlmInfoFactEmail")}</strong>{" "}
              <a href="mailto:info@predsense.ai">info@predsense.ai</a>
            </li>
            <li>
              <strong>{t("LlmInfoFactPhone")}</strong>{" "}
              <a href="tel:+436601599064">+43 660 1599064</a>
            </li>
            <li>
              <strong>{t("LlmInfoFactWebsite")}</strong>{" "}
              <a href={orgUrl}>{orgUrl}</a>
            </li>
            <li>
              <strong>{t("LlmInfoFactLinkedIn")}</strong>{" "}
              <a
                href="https://www.linkedin.com/company/predsense"
                rel="noopener noreferrer"
              >
                linkedin.com/company/predsense
              </a>
            </li>
          </ul>
        </section>

        <section aria-labelledby="llm-products-heading">
          <h2 id="llm-products-heading">{t("LlmInfoSectionProductsTitle")}</h2>
          <p>{t("LlmInfoSectionProductsIntro")}</p>
          <ul className="llm-info-facts">
            <li>
              <a href={`${orgUrl}/products/labeling`}>
                {orgUrl}/products/labeling
              </a>
              {" - "}
              {t("LlmInfoProductLabeling")}
            </li>
            <li>
              <a href={`${orgUrl}/products/anomaly-detection`}>
                {orgUrl}/products/anomaly-detection
              </a>
              {" - "}
              {t("LlmInfoProductAnomaly")}
            </li>
            <li>
              <a href={`${orgUrl}/pricing`}>{orgUrl}/pricing</a>
              {" - "}
              {t("LlmInfoProductPricing")}
            </li>
            <li>
              <a href="https://app.predsense.ai">https://app.predsense.ai</a>
              {" - "}
              {t("LlmInfoProductApp")}
            </li>
          </ul>
        </section>

        <section aria-labelledby="llm-topics-heading">
          <h2 id="llm-topics-heading">{t("LlmInfoSectionTopicsTitle")}</h2>
          <p>{t("LlmInfoSectionTopicsIntro")}</p>
          <ul className="llm-info-topics">
            <li>{t("LlmInfoTopic1")}</li>
            <li>{t("LlmInfoTopic2")}</li>
            <li>{t("LlmInfoTopic3")}</li>
            <li>{t("LlmInfoTopic4")}</li>
            <li>{t("LlmInfoTopic5")}</li>
            <li>{t("LlmInfoTopic6")}</li>
          </ul>
        </section>

        <section aria-labelledby="llm-cite-heading">
          <h2 id="llm-cite-heading">{t("LlmInfoSectionCiteTitle")}</h2>
          <p>{t("LlmInfoSectionCiteBody")}</p>
          <p>
            <a href={orgUrl}>{orgUrl}</a>
            {" · "}
            <a href={`${orgUrl}/about`}>{orgUrl}/about</a>
            {" · "}
            <a href={pageUrl}>{pageUrl}</a>
          </p>
        </section>
        </div>
      </div>
    </>
  );
};

export default LlmInfo;
